import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { getAppliedRoleListForDrCr } from '../../services/api'; 
import MetaTags from '../../components/MetaTags'; 
import DataTable from '../../components/DataTable'; 
import ApplicationStatsCards from '../../components/applicationList/ApplicationStatsCards';
import ApplicationMobileCard from '../../components/applicationList/ApplicationMobileCard';
import { getApplicationColumns } from '../../components/applicationList/ApplicationColumns';
import './ManageApplicationsPage.css'; 

const ManageApplicationList = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dashboardStats, setDashboardStats] = useState({
        totalSelected: 0,
        totalPending: 0,
        totalRejected: 0,
        totalSlotBooking: 0,
        totalWebUpload: 0
    });
    
    const navigate = useNavigate();

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await getAppliedRoleListForDrCr();
            const rawData = response.data.data || [];

            const transformedData = rawData.map(item => ({
                id: item.roleApplicationId,
                candidateName: item.candidateName || item.talent_name || 'Unknown',
                roleName: item.c_roleName  || item.c_role_name || 'N/A',
                projectName: item.projectName || 'N/A',
                auditionType: item.bookingType ? item.bookingType.replace('_', ' ') : 'N/A',
                verifyStatus: item.booking_status || 'Pending',
                status: item.result || 'Pending',
                month: item.updated_at ? new Date(item.updated_at).toLocaleString("default", { month: "short" }) : "N/A",
                date: item.updated_at ? new Date(item.updated_at).toLocaleDateString("en-GB") : "N/A",
            }));

            setData(transformedData);
            setDashboardStats({
                totalSelected: transformedData.filter(i => String(i.status).toLowerCase() === 'selected').length,
                totalRejected: transformedData.filter(i => String(i.status).toLowerCase() === 'rejected').length,
                totalPending: transformedData.filter(i => ['pending', 'applied', 'resubmit'].includes(String(i.status).toLowerCase())).length,
                totalSlotBooking: transformedData.filter(i => String(i.auditionType).toLowerCase().includes('slot')).length,
                totalWebUpload: transformedData.filter(i => String(i.auditionType).toLowerCase().includes('video')).length
            });
        } catch (err) {
            console.error(err);
            setError('Failed to load application data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { 
        loadData(); 
    }, [loadData]);

    const handleViewApp = useCallback((roleApplicationId) => {
        navigate(`/dashboard/manage-applications/${roleApplicationId}`);
    }, [navigate]);

    const columns = useMemo(() => getApplicationColumns(handleViewApp), [handleViewApp]);

    const renderMobileCard = useCallback((item) => (
        <ApplicationMobileCard key={item.id} item={item} handleViewApp={handleViewApp} />
    ), [handleViewApp]);

    if (loading) {
        return (
            <div className="text-center p-5 vh-100" style={{ backgroundColor: '#232323' }}>
                <Spinner animation="border" variant="danger" />
                <p className="text-white mt-2">Loading Applications...</p>
            </div>
        );
    }

    return (
        <div className="manage-applications-container p-2 p-md-3 animate-fade-in">
            <MetaTags title="Application List - BookMyAudition" />
            
            <div className="page-header mb-4">
                <h5 className="text-uppercase fw-bold color-b7343a pb-2 m-0 border-bottom border-secondary">
                    Application List
                </h5>
            </div>
            
            {error && <Alert variant="danger" className="bg-dark text-danger border-danger py-2 small">{error}</Alert>}

            <ApplicationStatsCards dataLength={data.length} dashboardStats={dashboardStats} />

            <DataTable 
                columns={columns} 
                data={data} 
                renderMobileItem={renderMobileCard} 
                searchPlaceholder="Search talent, role, or project..." 
            />
        </div>
    );
};

export default ManageApplicationList;